import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Grid,
  Paper,
  Slider,
  FormControlLabel,
  Switch,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Alert,
  useTheme
} from '@mui/material';
import { FilesetResolver, PoseLandmarker } from '@mediapipe/tasks-vision';
import { Camera } from '../Game/Camera';
import './ExerciseModelTrainer.css';
import './DialogOverrides.css'; // Import button override styles

const MUSCLE_GROUPS = [
  'Arms',
  'Shoulders',
  'Chest',
  'Back',
  'Core',
  'Legs',
  'Full Body'
];

const DETECTION_FOCUS = [
  { value: 'upperBody', label: 'Upper Body (Arms, Shoulders, Chest)' },
  { value: 'lowerBody', label: 'Lower Body (Legs, Hips)' },
  { value: 'core', label: 'Core (Abs, Back)' },
  { value: 'fullBody', label: 'Full Body Movement' }
];

const STEPS = ['Configure Exercise', 'Record Training Data', 'Train Model', 'Export & Deploy'];

function ExerciseModelTrainer() {
  const theme = useTheme();
  // Step management
  const [activeStep, setActiveStep] = useState(0);
  
  // Exercise configuration
  const [exerciseName, setExerciseName] = useState('');
  const [muscleGroup, setMuscleGroup] = useState('');
  const [detectionFocus, setDetectionFocus] = useState('upperBody');
  const [description, setDescription] = useState('');
  
  // Recording state
  const [recordingStatus, setRecordingStatus] = useState('idle'); // idle, setup, recording, paused, complete
  const [recordedFrames, setRecordedFrames] = useState([]);
  const [repetitionCount, setRepetitionCount] = useState(0);
  const [targetReps, setTargetReps] = useState(5);
  
  // Model training state
  const [trainingProgress, setTrainingProgress] = useState(0);
  const [modelStatus, setModelStatus] = useState('not_started'); // not_started, training, complete
  const [trainingData, setTrainingData] = useState([]);
  const [trainedModel, setTrainedModel] = useState(null);
  
  // Export options
  const [exportFormat, setExportFormat] = useState('tfjs');
  const [deployTarget, setDeployTarget] = useState('local');
  const [apiEndpoint, setApiEndpoint] = useState('');
  
  // MediaPipe and camera
  const [poseLandmarker, setPoseLandmarker] = useState(null);
  const [loading, setLoading] = useState(false);
  const [landmarks, setLandmarks] = useState(null);
  const [showLandmarks, setShowLandmarks] = useState(true);
  
  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const recordingTimerRef = useRef(null);
  
  // Initialize the pose landmarker on component mount
  useEffect(() => {
    const initializePoseLandmarker = async () => {
      setLoading(true);
      try {
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm"
        );
        
        const landmarker = await PoseLandmarker.createFromOptions(
          vision,
          {
            baseOptions: {
              modelAssetPath: "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_heavy/float16/1/pose_landmarker_heavy.task",
              delegate: "GPU"
            },
            runningMode: "VIDEO",
            numPoses: 1,
            minPoseDetectionConfidence: 0.5,
            minPosePresenceConfidence: 0.5,
            minTrackingConfidence: 0.5,
          }
        );
        
        setPoseLandmarker(landmarker);
      } catch (error) {
        console.error("Error initializing pose landmarker:", error);
      } finally {
        setLoading(false);
      }
    };
    
    initializePoseLandmarker();
    
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);
  
  // Set up camera when needed based on active step and recording status
  useEffect(() => {
    if (activeStep === 1 && recordingStatus === 'setup') {
      startCamera();
    } else if (activeStep !== 1) {
      stopCamera();
    }
  }, [activeStep, recordingStatus]);
  
  // Process video frames and collect landmarks
  useEffect(() => {
    if (activeStep === 1 && recordingStatus === 'recording' && poseLandmarker && videoRef.current) {
      const recordFrame = async () => {
        if (videoRef.current && poseLandmarker) {
          const timestamp = performance.now();
          const results = await poseLandmarker.detectForVideo(videoRef.current, timestamp);
          
          if (results.landmarks && results.landmarks.length > 0) {
            setLandmarks(results.landmarks[0]);
            
            // Store the landmarks for this frame
            const frameData = {
              timestamp,
              landmarks: JSON.parse(JSON.stringify(results.landmarks[0])),
              worldLandmarks: results.worldLandmarks ? 
                JSON.parse(JSON.stringify(results.worldLandmarks[0])) : null
            };
            
            setRecordedFrames(prev => [...prev, frameData]);
            setTrainingData(prev => [...prev, frameData]);
            
            // Draw landmarks on canvas if option is enabled
            if (showLandmarks) {
              drawLandmarks(results.landmarks[0]);
            }
            
            // Simple repetition counter logic - in a real app you'd use a more sophisticated detector
            // This is just a placeholder for UI demonstration
            if (recordedFrames.length % 30 === 0 && recordedFrames.length > 0) {
              setRepetitionCount(prev => prev + 1);
            }
          }
        }
      };
      
      recordingTimerRef.current = setInterval(recordFrame, 1000 / 30); // 30 FPS
      
      return () => {
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
        }
      };
    }
  }, [activeStep, recordingStatus, poseLandmarker, recordedFrames.length, showLandmarks]);

  // Step navigation
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    if (recordingStatus === 'recording') {
      pauseRecording();
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Camera controls
  const startCamera = async () => {
    try {
      if (videoRef.current) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });
        
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        
        // Set up canvas
        if (canvasRef.current) {
          const ctx = canvasRef.current.getContext('2d');
          canvasRef.current.width = videoRef.current.videoWidth || 640;
          canvasRef.current.height = videoRef.current.videoHeight || 480;
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
      }
    } catch (error) {
      console.error("Error starting camera:", error);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  // Recording controls
  const startRecording = () => {
    setRecordingStatus('recording');
    setRecordedFrames([]);
    setRepetitionCount(0);
  };

  const pauseRecording = () => {
    setRecordingStatus('paused');
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
  };

  const resumeRecording = () => {
    setRecordingStatus('recording');
  };

  const completeRecording = () => {
    pauseRecording();
    setRecordingStatus('complete');
  };

  // Draw landmarks on the canvas
  const drawLandmarks = (landmarks) => {
    if (!canvasRef.current || !landmarks) return;
    
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    
    // Draw connecting lines
    ctx.strokeStyle = '#00FF00';
    ctx.lineWidth = 2;
    
    // Connections for upper body
    const connections = [
      // Face connections
      [0, 1], [1, 2], [2, 3], [3, 7], [0, 4], [4, 5], [5, 6], [6, 8],
      // Torso
      [9, 10], [11, 12], [11, 23], [12, 24], [23, 24],
      // Left arm
      [11, 13], [13, 15], [15, 17], [15, 19], [15, 21], [17, 19],
      // Right arm
      [12, 14], [14, 16], [16, 18], [16, 20], [16, 22], [18, 20],
      // Left leg
      [23, 25], [25, 27], [27, 29], [27, 31], [29, 31],
      // Right leg
      [24, 26], [26, 28], [28, 30], [28, 32], [30, 32]
    ];
    
    for (const [i, j] of connections) {
      const start = landmarks[i];
      const end = landmarks[j];
      
      if (start && end) {
        ctx.beginPath();
        ctx.moveTo(start.x * canvasRef.current.width, start.y * canvasRef.current.height);
        ctx.lineTo(end.x * canvasRef.current.width, end.y * canvasRef.current.height);
        ctx.stroke();
      }
    }
    
    // Draw landmarks
    ctx.fillStyle = '#FF0000';
    for (const point of landmarks) {
      ctx.beginPath();
      ctx.arc(
        point.x * canvasRef.current.width,
        point.y * canvasRef.current.height,
        3,
        0,
        2 * Math.PI
      );
      ctx.fill();
    }
  };

  // Model training process
  const trainModel = async () => {
    setModelStatus('training');
    setTrainingProgress(0);
    
    // Simulate model training process
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 15;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        
        // Create the exercise model
        const exerciseModel = {
          id: `model-${Date.now()}`,
          name: exerciseName,
          muscleGroup: muscleGroup,
          detectionFocus: detectionFocus,
          description: description,
          repetitionCount: repetitionCount,
          sampleCount: recordedFrames.length,
          dateCreated: new Date().toISOString(),
          // For a real implementation, you would process the landmark data here
          // to create an actual model for exercise recognition
          trainingData: trainingData.map(frame => ({
            landmarks: frame.landmarks,
            worldLandmarks: frame.worldLandmarks
          }))
        };
        
        // Save the model to localStorage for now
        const existingModels = JSON.parse(localStorage.getItem('trainedExerciseModels') || '[]');
        localStorage.setItem('trainedExerciseModels', JSON.stringify([...existingModels, exerciseModel]));
        
        setTrainedModel(exerciseModel);
        setModelStatus('complete');
      }
      setTrainingProgress(Math.min(Math.round(progress), 100));
    }, 200);
  };

  // Export trained model
  const exportModel = (format, target) => {
    // In a real implementation, this would convert the model to the specified format
    // and deploy it to the target platform
    console.log(`Exporting model as ${format} to ${target}`, trainedModel);
    
    if (target === 'api') {
      // Simulate API deployment
      alert(`Model "${exerciseName}" would be deployed to API endpoint: ${apiEndpoint}`);
    } else if (target === 'cdn') {
      // Simulate CDN deployment
      alert(`Model "${exerciseName}" would be deployed to CDN for public access`);
    } else {
      // Local export - generate a download
      const modelData = JSON.stringify(trainedModel, null, 2);
      const blob = new Blob([modelData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exerciseName.replace(/\s+/g, '_').toLowerCase()}_model.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // Reset the trainer
  const resetTrainer = () => {
    setActiveStep(0);
    setExerciseName('');
    setMuscleGroup('');
    setDetectionFocus('upperBody');
    setDescription('');
    setRecordingStatus('idle');
    setRecordedFrames([]);
    setRepetitionCount(0);
    setTargetReps(5);
    setTrainingProgress(0);
    setModelStatus('not_started');
    setTrainingData([]);
    setTrainedModel(null);
    setExportFormat('tfjs');
    setDeployTarget('local');
  };

  // Render the appropriate content for each step
  const renderStepContent = (step) => {
    switch (step) {
      case 0: // Configure Exercise
        return (
          <Paper sx={{ p: 3, bgcolor: theme.palette.mode === 'dark' ? '#232323' : '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom>
              Exercise Configuration
            </Typography>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Exercise Name"
                  fullWidth
                  variant="outlined"
                  value={exerciseName}
                  onChange={(e) => setExerciseName(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="muscle-group-label">Primary Muscle Group</InputLabel>
                  <Select
                    labelId="muscle-group-label"
                    value={muscleGroup}
                    onChange={(e) => setMuscleGroup(e.target.value)}
                    label="Primary Muscle Group"
                    required
                  >
                    {MUSCLE_GROUPS.map(group => (
                      <MenuItem key={group} value={group}>{group}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="detection-focus-label">Detection Focus</InputLabel>
                  <Select
                    labelId="detection-focus-label"
                    value={detectionFocus}
                    onChange={(e) => setDetectionFocus(e.target.value)}
                    label="Detection Focus"
                  >
                    {DETECTION_FOCUS.map(focus => (
                      <MenuItem key={focus.value} value={focus.value}>{focus.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description (Optional)"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography gutterBottom>
                  Target Repetitions for Training
                </Typography>
                <Slider
                  value={targetReps}
                  onChange={(e, value) => setTargetReps(value)}
                  valueLabelDisplay="auto"
                  step={1}
                  marks
                  min={3}
                  max={10}
                />
                <Typography variant="caption" color="text.secondary">
                  We recommend recording at least 5 repetitions for optimal training.
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        );

      case 1: // Record Training Data
        return (
          <Paper sx={{ p: 3, bgcolor: theme.palette.mode === 'dark' ? '#232323' : '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom>
              Record Exercise Movements
            </Typography>
            
            {recordingStatus === 'idle' && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" paragraph>
                  Position yourself in front of the camera and prepare to perform the exercise.
                  You'll need to record at least {targetReps} repetitions for accurate model training.
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={() => setRecordingStatus('setup')}
                >
                  Setup Camera
                </Button>
              </Box>
            )}
            
            {(recordingStatus === 'setup' || recordingStatus === 'recording' || recordingStatus === 'paused') && (
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                      <video 
                        ref={videoRef}
                        style={{ width: '100%', maxHeight: '500px', backgroundColor: '#000' }}
                        playsInline
                      />
                      <canvas 
                        ref={canvasRef}
                        style={{ 
                          position: 'absolute', 
                          top: 0, 
                          left: 0,
                          width: '100%', 
                          height: '100%',
                          zIndex: 10
                        }}
                      />
                      <Box 
                        sx={{ 
                          position: 'absolute', 
                          top: 10, 
                          left: 10, 
                          bgcolor: 'rgba(0,0,0,0.5)',
                          color: 'white',
                          p: 1,
                          borderRadius: 1,
                          zIndex: 20
                        }}
                      >
                        <Typography variant="body2">
                          Repetitions: {repetitionCount} / {targetReps}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="subtitle1" gutterBottom>
                          Recording Controls
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                          {recordingStatus === 'setup' && (
                            <Button 
                              variant="contained" 
                              color="primary" 
                              fullWidth
                              onClick={startRecording}
                            >
                              Start Recording
                            </Button>
                          )}
                          
                          {recordingStatus === 'recording' && (
                            <>
                              <Button 
                                variant="outlined" 
                                color="primary" 
                                fullWidth
                                onClick={pauseRecording}
                              >
                                Pause
                              </Button>
                              <Button 
                                variant="contained" 
                                color="secondary" 
                                fullWidth
                                onClick={completeRecording}
                                disabled={repetitionCount < targetReps}
                              >
                                Complete ({repetitionCount}/{targetReps})
                              </Button>
                            </>
                          )}
                          
                          {recordingStatus === 'paused' && (
                            <>
                              <Button 
                                variant="contained" 
                                color="primary" 
                                fullWidth
                                onClick={resumeRecording}
                              >
                                Resume
                              </Button>
                              <Button 
                                variant="contained" 
                                color="secondary" 
                                fullWidth
                                onClick={completeRecording}
                                disabled={repetitionCount < targetReps}
                              >
                                Complete ({repetitionCount}/{targetReps})
                              </Button>
                            </>
                          )}
                        </Box>
                      </Box>
                      
                      <Box sx={{ mt: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={showLandmarks}
                              onChange={(e) => setShowLandmarks(e.target.checked)}
                              color="primary"
                            />
                          }
                          label="Show Pose Landmarks"
                        />
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          Frames captured: {recordedFrames.length}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
            
            {recordingStatus === 'complete' && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="primary" gutterBottom>
                  Recording Complete!
                </Typography>
                <Typography variant="body1">
                  You've successfully recorded {repetitionCount} repetitions with {recordedFrames.length} frames.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                  Click "Next" to proceed to model training.
                </Typography>
                <Button 
                  variant="outlined" 
                  color="primary"
                  onClick={() => setRecordingStatus('setup')}
                >
                  Record Again
                </Button>
              </Box>
            )}
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Tips for Best Results
              </Typography>
              <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                <li>Choose a well-lit environment with no backlighting</li>
                <li>Position yourself fully in the camera frame</li>
                <li>Maintain consistent speed for each repetition</li>
                <li>Wear clothing that contrasts with the background</li>
              </ul>
            </Box>
          </Paper>
        );
      case 2: // Train Model
        return (
          <Paper sx={{ p: 3, bgcolor: theme.palette.mode === 'dark' ? '#232323' : '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom>
              Train Exercise Model
            </Typography>
            
            {modelStatus === 'not_started' && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body1" paragraph>
                  Your training data is ready to be processed. The model will learn to recognize this exercise based on the recorded movements.
                </Typography>
                <Typography variant="body2" paragraph>
                  Summary:
                </Typography>
                <Box sx={{ bgcolor: theme.palette.background.paper, p: 2, borderRadius: 1, mb: 3, textAlign: 'left' }}>
                  <Typography><strong>Exercise Name:</strong> {exerciseName}</Typography>
                  <Typography><strong>Muscle Group:</strong> {muscleGroup}</Typography>
                  <Typography><strong>Detection Focus:</strong> {DETECTION_FOCUS.find(f => f.value === detectionFocus)?.label}</Typography>
                  <Typography><strong>Frames Recorded:</strong> {recordedFrames.length}</Typography>
                  <Typography><strong>Repetitions:</strong> {repetitionCount}</Typography>
                </Box>
                
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={trainModel}
                  disabled={recordedFrames.length < 30}
                >
                  Train Model
                </Button>
                {recordedFrames.length < 30 && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    Not enough training data. Please record at least 30 frames.
                  </Typography>
                )}
              </Box>
            )}
            
            {modelStatus === 'training' && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress 
                  variant="determinate" 
                  value={trainingProgress} 
                  size={80} 
                  thickness={4}
                  sx={{ mb: 2 }}
                />
                <Typography variant="h6">
                  Training Model: {trainingProgress}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This may take a few moments...
                </Typography>
                
                <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                  <Typography variant="body2" align="left">
                    <strong>What's happening:</strong> The system is analyzing your movement patterns,
                    identifying key poses, and building a model that can recognize this exercise.
                    This includes:
                  </Typography>
                  <ul style={{ textAlign: 'left' }}>
                    <li>Processing {recordedFrames.length} frames of pose data</li>
                    <li>Extracting distinctive movement patterns</li>
                    <li>Optimizing for real-time detection</li>
                    <li>Building a deployable model package</li>
                  </ul>
                </Box>
              </Box>
            )}
            
            {modelStatus === 'complete' && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h6" color="primary" gutterBottom>
                  Exercise Model Created Successfully!
                </Typography>
                <Typography variant="body1" paragraph>
                  Your custom exercise "{exerciseName}" model has been trained and is ready for deployment.
                </Typography>
                <Typography variant="body2" paragraph>
                  Click "Next" to export your model and configure deployment options.
                </Typography>
                
                <Box sx={{ mt: 3, p: 2, bgcolor: theme.palette.background.paper, borderRadius: 1, textAlign: 'left' }}>
                  <Typography variant="subtitle1" gutterBottom>Model Information:</Typography>
                  <Typography><strong>Model ID:</strong> {trainedModel?.id}</Typography>
                  <Typography><strong>Created:</strong> {new Date().toLocaleString()}</Typography>
                  <Typography><strong>Input Samples:</strong> {recordedFrames.length} frames</Typography>
                  <Typography><strong>Model Size:</strong> ~{Math.round(JSON.stringify(trainedModel || {}).length / 1024)} KB</Typography>
                </Box>
              </Box>
            )}
          </Paper>
        );
      case 3: // Export & Deploy
        return (
          <Paper sx={{ p: 3, bgcolor: theme.palette.mode === 'dark' ? '#232323' : '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom>
              Export & Deploy Model
            </Typography>
            
            {modelStatus !== 'complete' ? (
              <Alert severity="warning" sx={{ mb: 3 }}>
                You need to complete model training before you can export or deploy.
              </Alert>
            ) : (
              <>
                <Typography variant="body1" paragraph>
                  Your model is ready to be exported or deployed. Choose your preferred format and deployment target.
                </Typography>
                
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="export-format-label">Export Format</InputLabel>
                      <Select
                        labelId="export-format-label"
                        value={exportFormat}
                        onChange={(e) => setExportFormat(e.target.value)}
                        label="Export Format"
                      >
                        <MenuItem value="tfjs">TensorFlow.js (Web)</MenuItem>
                        <MenuItem value="tflite">TensorFlow Lite (Mobile)</MenuItem>
                        <MenuItem value="json">Raw JSON (Development)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="deploy-target-label">Deployment Target</InputLabel>
                      <Select
                        labelId="deploy-target-label"
                        value={deployTarget}
                        onChange={(e) => setDeployTarget(e.target.value)}
                        label="Deployment Target"
                      >
                        <MenuItem value="local">Local Download</MenuItem>
                        <MenuItem value="cdn">CDN Hosting</MenuItem>
                        <MenuItem value="api">API Endpoint</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {deployTarget === 'api' && (
                    <Grid item xs={12}>
                      <TextField
                        label="API Endpoint URL"
                        fullWidth
                        variant="outlined"
                        value={apiEndpoint}
                        onChange={(e) => setApiEndpoint(e.target.value)}
                        placeholder="https://your-api-endpoint.com/models"
                        helperText="Enter the API endpoint where the model will be deployed"
                      />
                    </Grid>
                  )}
                  
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => exportModel(exportFormat, deployTarget)}
                        size="large"
                        sx={{ px: 4, py: 1 }}
                      >
                        {deployTarget === 'local' ? 'Download Model' : 
                        deployTarget === 'cdn' ? 'Deploy to CDN' : 
                        'Deploy to API'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 4, p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Integration Instructions
                  </Typography>
                  <Typography variant="body2" paragraph>
                    To use this exercise model in your application:
                  </Typography>
                  <Box component="pre" sx={{ 
                    bgcolor: theme.palette.mode === 'dark' ? '#2d2d2d' : '#f5f5f5',
                    p: 2,
                    borderRadius: 1,
                    overflowX: 'auto',
                    fontSize: '0.8rem'
                  }}>
                    {`// Load the exercise model
const model = await tf.loadLayersModel('${deployTarget === 'cdn' ? 
                      'https://cdn.example.com/models/' + 
                      exerciseName.replace(/\s+/g, '_').toLowerCase() : 
                      deployTarget === 'api' ? 
                      apiEndpoint || 'https://api.example.com/models/' + 
                      exerciseName.replace(/\s+/g, '_').toLowerCase() : 
                      'path/to/downloaded/model/' + 
                      exerciseName.replace(/\s+/g, '_').toLowerCase()}');

// Use the model for inference with pose landmarks
function detectExercise(poseLandmarks) {
  const input = preprocessLandmarks(poseLandmarks);
  const prediction = model.predict(input);
  return interpretPrediction(prediction);
}`}
                  </Box>
                  
                  <Button 
                    variant="text" 
                    color="primary"
                    onClick={resetTrainer}
                    className="exercise-dialog-actions"
                    sx={{ 
                      mt: 3,
                      bgcolor: 'transparent',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.08)'
                      }
                    }}
                  >
                    Create Another Exercise Model
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box className="exercise-model-trainer">
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {renderStepContent(activeStep)}
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mt: 3,
            '& .MuiButton-root': {
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.08)'
              }
            }
          }}>
            <Button
              variant="outlined"
              disabled={activeStep === 0 || modelStatus === 'training'}
              onClick={handleBack}
            >
              Back
            </Button>
            <Box>
              {activeStep < STEPS.length - 1 && (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={(activeStep === 0 && !exerciseName) || 
                           (activeStep === 1 && recordingStatus !== 'complete') ||
                           (activeStep === 2 && modelStatus !== 'complete')}
                  sx={{ 
                    bgcolor: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark
                    }
                  }}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

export default ExerciseModelTrainer;
